import type { AdminSection } from '../admin/sections';
import type { WebGuide } from '../modules/define';

export interface KernelGuide extends WebGuide {
  /** Guides of administration pages are visible to those who may open one of them. */
  sections?: readonly AdminSection['id'][];
}

/** Guides of the kernel. Module guides come from `guides` of their web module. */
export const KERNEL_GUIDES: readonly KernelGuide[] = [
  {
    id: 'core.start',
    title: 'help.guides.start.title',
    summary: 'help.guides.start.summary',
    pages: ['/'],
    content: {
      de: () => import('./content/start.de.md?raw'),
      en: () => import('./content/start.en.md?raw'),
    },
  },
  {
    id: 'core.search',
    title: 'help.guides.search.title',
    summary: 'help.guides.search.summary',
    pages: ['/search'],
    content: {
      de: () => import('./content/search.de.md?raw'),
      en: () => import('./content/search.en.md?raw'),
    },
  },
  {
    id: 'core.files',
    title: 'help.guides.files.title',
    summary: 'help.guides.files.summary',
    content: {
      de: () => import('./content/files.de.md?raw'),
      en: () => import('./content/files.en.md?raw'),
    },
  },
  {
    id: 'core.activity',
    title: 'help.guides.activity.title',
    summary: 'help.guides.activity.summary',
    content: {
      de: () => import('./content/activity.de.md?raw'),
      en: () => import('./content/activity.en.md?raw'),
    },
  },
  {
    id: 'core.for-me',
    title: 'help.guides.forMe.title',
    summary: 'help.guides.forMe.summary',
    pages: ['/for-me'],
    content: {
      de: () => import('./content/for-me.de.md?raw'),
      en: () => import('./content/for-me.en.md?raw'),
    },
  },
  {
    id: 'core.workflows',
    title: 'help.guides.workflows.title',
    summary: 'help.guides.workflows.summary',
    content: {
      de: () => import('./content/workflows.de.md?raw'),
      en: () => import('./content/workflows.en.md?raw'),
    },
  },
  {
    id: 'core.sharing',
    title: 'help.guides.sharing.title',
    summary: 'help.guides.sharing.summary',
    pages: ['/admin/shares'],
    content: {
      de: () => import('./content/sharing.de.md?raw'),
      en: () => import('./content/sharing.en.md?raw'),
    },
  },
  {
    id: 'core.profile',
    title: 'help.guides.profile.title',
    summary: 'help.guides.profile.summary',
    pages: ['/profile'],
    content: {
      de: () => import('./content/profile.de.md?raw'),
      en: () => import('./content/profile.en.md?raw'),
    },
  },
  {
    id: 'core.accounts',
    title: 'help.guides.accounts.title',
    summary: 'help.guides.accounts.summary',
    sections: ['accounts', 'groups'],
    pages: [
      '/admin/accounts',
      '/admin/accounts/$accountId',
      '/admin/groups',
      '/admin/groups/$groupId',
    ],
    content: {
      de: () => import('./content/accounts.de.md?raw'),
      en: () => import('./content/accounts.en.md?raw'),
    },
  },
  {
    id: 'core.roles',
    title: 'help.guides.roles.title',
    summary: 'help.guides.roles.summary',
    sections: ['roles'],
    pages: ['/admin/roles', '/admin/roles/new', '/admin/roles/report', '/admin/roles/$roleId'],
    content: {
      de: () => import('./content/roles.de.md?raw'),
      en: () => import('./content/roles.en.md?raw'),
    },
  },
  {
    id: 'core.sso',
    title: 'help.guides.sso.title',
    summary: 'help.guides.sso.summary',
    sections: ['sso'],
    pages: ['/admin/sso'],
    content: {
      de: () => import('./content/sso.de.md?raw'),
      en: () => import('./content/sso.en.md?raw'),
    },
  },
  {
    id: 'core.notifications',
    title: 'help.guides.notifications.title',
    summary: 'help.guides.notifications.summary',
    sections: ['notifications'],
    pages: ['/admin/notifications'],
    content: {
      de: () => import('./content/notifications.de.md?raw'),
      en: () => import('./content/notifications.en.md?raw'),
    },
  },
  {
    id: 'core.integrations',
    title: 'help.guides.integrations.title',
    summary: 'help.guides.integrations.summary',
    sections: ['integrations'],
    pages: ['/admin/integrations'],
    content: {
      de: () => import('./content/integrations.de.md?raw'),
      en: () => import('./content/integrations.en.md?raw'),
    },
  },
  {
    id: 'core.custom-fields',
    title: 'help.guides.customFields.title',
    summary: 'help.guides.customFields.summary',
    sections: ['customFields'],
    pages: ['/admin/custom-fields'],
    content: {
      de: () => import('./content/custom-fields.de.md?raw'),
      en: () => import('./content/custom-fields.en.md?raw'),
    },
  },
  {
    id: 'core.privacy',
    title: 'help.guides.privacy.title',
    summary: 'help.guides.privacy.summary',
    sections: ['privacy'],
    pages: ['/admin/privacy'],
    content: {
      de: () => import('./content/privacy.de.md?raw'),
      en: () => import('./content/privacy.en.md?raw'),
    },
  },
  {
    id: 'core.offline',
    title: 'help.guides.offline.title',
    summary: 'help.guides.offline.summary',
    content: {
      de: () => import('./content/offline.de.md?raw'),
      en: () => import('./content/offline.en.md?raw'),
    },
  },
  {
    id: 'core.ai',
    title: 'help.guides.ai.title',
    summary: 'help.guides.ai.summary',
    sections: ['ai'],
    pages: ['/admin/ai'],
    content: {
      de: () => import('./content/ai.de.md?raw'),
      en: () => import('./content/ai.en.md?raw'),
    },
  },
  {
    id: 'core.company',
    title: 'help.guides.company.title',
    summary: 'help.guides.company.summary',
    sections: ['settings', 'branding', 'companyModules'],
    pages: ['/admin/settings', '/admin/branding', '/admin/modules'],
    content: {
      de: () => import('./content/company.de.md?raw'),
      en: () => import('./content/company.en.md?raw'),
    },
  },
  {
    id: 'core.audit',
    title: 'help.guides.audit.title',
    summary: 'help.guides.audit.summary',
    sections: ['audit', 'platformAudit'],
    pages: ['/admin/audit', '/admin/platform/audit'],
    content: {
      de: () => import('./content/audit.de.md?raw'),
      en: () => import('./content/audit.en.md?raw'),
    },
  },
  {
    id: 'core.platform',
    title: 'help.guides.platform.title',
    summary: 'help.guides.platform.summary',
    sections: ['companies', 'modules', 'platformAccess'],
    pages: ['/admin/platform/companies', '/admin/platform/modules', '/admin/platform/access'],
    content: {
      de: () => import('./content/platform.de.md?raw'),
      en: () => import('./content/platform.en.md?raw'),
    },
  },
  {
    id: 'core.operations',
    title: 'help.guides.operations.title',
    summary: 'help.guides.operations.summary',
    sections: ['operations'],
    pages: ['/admin/platform/operations'],
    content: {
      de: () => import('./content/operations.de.md?raw'),
      en: () => import('./content/operations.en.md?raw'),
    },
  },
];
